import { memo, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';



/**
 * Dual-engine A/B Audio Player with error handling and smooth transitions
 */
const ABPlayer = memo(({ sourceA, sourceB, labelA = "WITH ROOM", labelB = "ROOM OFF" }) => {
  const [activeSource, setActiveSource] = useState('A');
  // const [isPlaying, setIsPlaying] = useState(false);
  const audioARef = useRef(null);
  const audioBRef = useRef(null);
  const containerRef = useRef(null);
  const syncingRef = useRef(false);
  
    // Ensure immediate cleanup when unmounting
  useEffect(() => {
    const currentAudioRef = audioARef.current;
    const currentBudioRef = audioBRef.current;
    return () => {
      // Immediate cleanup when component unmounts
      if (currentAudioRef) {
        currentAudioRef.pause();
        currentAudioRef.src = '';
      }
      if (currentBudioRef) {
        currentBudioRef.pause();
        currentBudioRef.src = '';
      }
    };
  }, []);


  // Set up audio elements and synchronization with enhanced error handling
  useEffect(() => {
    // Safety check for refs and sources
    if (!audioARef.current || !audioBRef.current || !sourceA || !sourceB) return;

    // Initial setup
    audioARef.current.src = sourceA;
    audioBRef.current.src = sourceB;
    
    // Safe synchronization function that checks for null/undefined at every step
    const syncTime = (sourceEvent) => {
      // Prevent recursive syncing
      if (syncingRef.current) return;
      
      // Verify the event has a target and our refs are still valid
      if (!sourceEvent || !sourceEvent.target || !audioARef.current || !audioBRef.current) return;
      
      syncingRef.current = true;
      
      try {
        const sourceEl = sourceEvent.target;
        const targetEl = sourceEl === audioARef.current ? audioBRef.current : audioARef.current;
        
        // Check if both elements exist and have valid currentTime properties
        if (sourceEl && targetEl && 
            typeof sourceEl.currentTime === 'number' && 
            typeof targetEl.currentTime === 'number') {
          
          // Only sync if difference is significant
          if (Math.abs(sourceEl.currentTime - targetEl.currentTime) > 0.1) {
            targetEl.currentTime = sourceEl.currentTime;
          }
        }
      } catch (err) {
        console.error("Error during time sync:", err);
      } finally {
        syncingRef.current = false;
      }
    };

    // Safe play synchronization with error handling
    const syncPlay = (sourceEvent) => {
      if (syncingRef.current) return;
      if (!sourceEvent || !sourceEvent.target || !audioARef.current || !audioBRef.current) return;
      
      syncingRef.current = true;
      
      try {
        const sourceEl = sourceEvent.target;
        const targetEl = sourceEl === audioARef.current ? audioBRef.current : audioARef.current;
        
        if (sourceEl && targetEl && typeof sourceEl.currentTime === 'number') {
          const sourceTime = sourceEl.currentTime;
          
          if (targetEl.paused) {
            targetEl.currentTime = sourceTime;
            const playPromise = targetEl.play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                console.error("Error during sync playback:", error);
              });
            }
          }
        }
        
        // setIsPlaying(true);
      } catch (err) {
        console.error("Error during play sync:", err);
      } finally {
        syncingRef.current = false;
      }
    };
    
    // Safe pause synchronization with error handling
    const syncPause = (sourceEvent) => {
      if (syncingRef.current) return;
      if (!sourceEvent || !sourceEvent.target || !audioARef.current || !audioBRef.current) return;
      
      syncingRef.current = true;
      
      try {
        const sourceEl = sourceEvent.target;
        const targetEl = sourceEl === audioARef.current ? audioBRef.current : audioARef.current;
        
        if (targetEl && typeof targetEl.pause === 'function') {
          targetEl.pause();
        }
        
        // setIsPlaying(false);
      } catch (err) {
        console.error("Error during pause sync:", err);
      } finally {
        syncingRef.current = false;
      }
    };
    
    // Handle playback ended
    const handleEnded = () => {
      // setIsPlaying(false);
    };
    
    // Attach event listeners with try-catch for safety
    try {
      audioARef.current.addEventListener('timeupdate', syncTime);
      audioBRef.current.addEventListener('timeupdate', syncTime);
      audioARef.current.addEventListener('play', syncPlay);
      audioBRef.current.addEventListener('play', syncPlay);
      audioARef.current.addEventListener('pause', syncPause);
      audioBRef.current.addEventListener('pause', syncPause);
      audioARef.current.addEventListener('ended', handleEnded);
      audioBRef.current.addEventListener('ended', handleEnded);
      
      // Set initial volume
      audioARef.current.volume = 1.0;
      audioBRef.current.volume = 0.0;
    } catch (err) {
      console.error("Error setting up audio event listeners:", err);
    }
    
    // Clean up with try-catch for safety
    return () => {
      try {
        if (audioARef.current) {
          audioARef.current.removeEventListener('timeupdate', syncTime);
          audioARef.current.removeEventListener('play', syncPlay);
          audioARef.current.removeEventListener('pause', syncPause);
          audioARef.current.removeEventListener('ended', handleEnded);
        }
        
        if (audioBRef.current) {
          audioBRef.current.removeEventListener('timeupdate', syncTime);
          audioBRef.current.removeEventListener('play', syncPlay);
          audioBRef.current.removeEventListener('pause', syncPause);
          audioBRef.current.removeEventListener('ended', handleEnded);
        }
      } catch (err) {
        console.error("Error cleaning up audio event listeners:", err);
      }
    };
  }, [sourceA, sourceB]);
  
  // Audio crossfade effect with error handling
  useEffect(() => {
    if (!audioARef.current || !audioBRef.current) return;
    
    const fadeSteps = 20;
    const fadeInterval = 15;
    
    let currentStep = 0;
    const fadeTo = activeSource === 'A' ? 1.0 : 0.0;
    const fadeFrom = activeSource === 'A' ? 0.0 : 1.0;
    
    try {
      const initialVolumeA = audioARef.current.volume;
      const initialVolumeB = audioBRef.current.volume;
      const deltaA = fadeTo - initialVolumeA;
      const deltaB = fadeFrom - initialVolumeB;
      
      const intervalId = setInterval(() => {
        currentStep++;
        
        try {
          if (currentStep <= fadeSteps && audioARef.current && audioBRef.current) {
            const progress = currentStep / fadeSteps;
            const easedProgress = 0.5 - 0.5 * Math.cos(Math.PI * progress);
            
            audioARef.current.volume = initialVolumeA + deltaA * easedProgress;
            audioBRef.current.volume = initialVolumeB + deltaB * easedProgress;
          } else {
            if (audioARef.current) audioARef.current.volume = fadeTo;
            if (audioBRef.current) audioBRef.current.volume = fadeFrom;
            clearInterval(intervalId);
          }
        } catch (err) {
          console.error("Error during volume fade:", err);
          clearInterval(intervalId);
        }
      }, fadeInterval);
      
      return () => clearInterval(intervalId);
    } catch (err) {
      console.error("Error setting up volume fade:", err);
      return () => {}; // Empty cleanup function
    }
  }, [activeSource]);
  
  // Toggle between sources with error handling
  const handleToggleSource = (source, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (source !== activeSource) {
      setActiveSource(source);
    }
  };
  
  // Container click handler
  const handleContainerMouseDown = (e) => {
    e.stopPropagation();
  };
  
  return (
    <PlayerWrapper ref={containerRef} onMouseDown={handleContainerMouseDown}>
      {/* Audio container with transitions */}
      <AudioContainer>
        <TransitionAudio 
          active={activeSource === 'A'}
          ref={audioARef}
          controls
          onMouseDown={(e) => e.stopPropagation()}
        />
        
        <TransitionAudio 
          active={activeSource === 'B'}
          ref={audioBRef}
          controls
          onMouseDown={(e) => e.stopPropagation()}
        />
      </AudioContainer>
      
      {/* A/B Toggle Buttons */}
      <ControlsRow>
        <SourceButton 
          active={activeSource === 'A'} 
          onMouseDown={(e) => handleToggleSource('A', e)}
          isLeft
        >
          {labelA}
        </SourceButton>
        
        <SourceButton 
          active={activeSource === 'B'} 
          onMouseDown={(e) => handleToggleSource('B', e)}
          isRight
        >
          {labelB}
        </SourceButton>
      </ControlsRow>
    </PlayerWrapper>
  );
});





// Styled components with transition effects
const PlayerWrapper = styled.div.attrs(props => ({
  className: 'ABPlayer',
}))`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f2f2f2;
  border-radius: 12px;
`;  

// Container for the audio elements
const AudioContainer = styled.div.attrs(props => ({
  className: 'AudioContainer',
}))`
  position: relative;
  width: 100%;
  height: 54px; 
`;

// Audio element with transition effects
const TransitionAudio = styled.audio.attrs(props => ({
  className: 'TransitionAudio',
}))`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  outline: none;
  opacity: ${props => props.active ? 1 : 0};
  z-index: ${props => props.active ? 2 : 1};
  pointer-events: ${props => props.active ? 'auto' : 'none'};
  transition: opacity 0.3s ease-in-out;
`;

const ControlsRow = styled.div.attrs(props => ({
  className: 'ControlsRow',
}))`
  display: flex;
  width: 80%;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const SourceButton = styled.button.attrs(props => ({
  className: 'SourceButton',
  }))`
  font-size: 0.9rem;
  text-transform: uppercase;
  font-weight: bold;
  border: none;
  color: ${props => props.active ? '#fff' : 'var(--app-medium-gray)'};
  background-color: ${props => props.active ? 'var(--app-red)' : 'var(--app-light-gray)'};
  padding: 4px 8px;
  border: 1px solid var(--app-dark-gray);
  border-radius: ${props => props.isLeft ? '8px 0 0 8px' : props.isRight ? '0 8px 8px 0' : '8px'};
  cursor: pointer;
  flex: 1;
`;

export default ABPlayer;